'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowRight, ChevronDown } from 'lucide-react';

const faqItems = [
  {
    id: 'faq-1',
    question: 'What is Panorama Towers?',
    answer: 'Panorama Towers is a landmark twin high-rise residential community on Dean Martin Drive in Las Vegas — two 33-story glass towers with 659 total residences, designed by Klai Juba Wald and recognized with an AIA Merit Award. It offers luxury high-rise living with a full amenity package, guarded entry, valet, and a real resident community.',
  },
  {
    id: 'faq-2',
    question: 'Is Panorama a good choice for a second home?',
    answer: 'Panorama is exceptionally well-suited for second-home ownership. The building is designed for lock-and-leave convenience — valet, concierge, guarded entry, and a low-maintenance lifestyle. Many current residents use Panorama as their Las Vegas base, traveling freely and returning to everything in order.',
  },
  {
    id: 'faq-3',
    question: 'What amenities are available?',
    answer: 'Panorama offers a comprehensive amenity package including a fitness center, resort-style pool, spa, massage rooms, valet, guarded entry, concierge services, lobby and lounge areas, and resident storage. The building also includes townhome residences with private entries.',
  },
  {
    id: 'faq-4',
    question: 'Can renters join the community platform?',
    answer: 'Yes. Renters are full community members. The platform is open to all verified Panorama residents — owners and renters alike. Residency is the only requirement for access.',
  },
  {
    id: 'faq-5',
    question: 'Is this platform official?',
    answer: 'No. This is an independent, resident-operated platform. It is not affiliated with Panorama Towers management, the condominium association, or any official property entity. It exists because residents wanted a better way to connect, share resources, and build community.',
  },
];

export default function FAQPreviewSection() {
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <section className="py-24" style={{ background: '#0C0A16' }}>
      <div className="neon-divider mb-0" />
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left — copy */}
          <div>
            <p className="text-[12px] font-semibold tracking-[0.12em] uppercase mb-3" style={{ color: '#FF3DF5' }}>
              FAQ
            </p>
            <h2 className="font-display font-bold text-[36px] sm:text-[42px] leading-tight tracking-tight mb-5" style={{ color: '#F2F4FF' }}>
              Common questions, answered.
            </h2>
            <p className="text-[16px] leading-relaxed mb-8" style={{ color: 'rgba(167,163,196,0.80)' }}>
              Whether you're considering Panorama as a home, already a resident, or curious about the community platform — here are the most common questions.
            </p>
            <Link href="/faq" className="btn-secondary inline-flex items-center gap-2">
              View FAQ
              <ArrowRight size={14} />
            </Link>
          </div>

          {/* Right — accordion */}
          <div className="space-y-2">
            {faqItems?.map((item) => {
              const isOpen = openId === item?.id;
              return (
                <div
                  key={item?.id}
                  className="neon-card overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between p-5 text-left"
                    onClick={() => setOpenId(isOpen ? null : item?.id)}
                  >
                    <span className="font-semibold text-[14px] pr-4 leading-snug" style={{ color: '#F2F4FF' }}>
                      {item?.question}
                    </span>
                    <ChevronDown
                      size={16}
                      style={{
                        color: '#7A5CFF',
                        flexShrink: 0,
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                      }}
                    />
                  </button>
                  {isOpen && (
                    <div
                      className="px-5 pb-5 text-[13.5px] leading-relaxed"
                      style={{ color: 'rgba(167,163,196,0.80)', borderTop: '1px solid rgba(255,255,255,0.06)' }}
                    >
                      <div className="pt-4">{item?.answer}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
